import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import {
  Box,
  Button,
  // Checkbox,
  PageHeader,
  TableSkeleton,
  generateTableFromSchema,
  useTable,
  Popover,
  Picto,
  Pagination,
  // @ts-ignore
} from '@zolteam/zol-admin-ui';

// Services
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  QUERY_KEY_GARAGE_EXCLUSIONS,
  useGetGarageExclusionsList,
} from '../../services/garage-exclusions/garageExclusionsQueries';
import {
  postExcludeGarage,
  postReintegrateGarage,
} from '../../services/garage-exclusions/garageExclusionsService';

const GarageExclusionListSchema = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'Garage Exclusion',
  properties: {
    requestedBy: {
      type: 'string',
      description: 'Demandé par',
      x_layout: '2/1/2/13',
    },
    exclusionReason: {
      type: 'string',
      description: "Raison de l'exclusion",
      x_layout: '1/7/1/13',
    },
    isVisible: {
      type: 'boolean',
      description: 'Visible',
      x_layout: '3/1/3/13',
    },
    isTreated: {
      type: 'boolean',
      description: 'Traité',
      x_layout: '4/1/4/13',
    },
  },
  additionalProperties: false,
};

/**
 * GarageExclusionList component.
 * @returns {JSX.Element}
 */
function GarageExclusionList() {
  // Hooks
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // -- search-params
  const [searchParam, setSearchParams] = useSearchParams();
  const pageNumber = searchParam.get('pageNumber') || 1;
  const pageSize = searchParam.get('pageSize') || 10;
  const sort = searchParam.get('sort') || 'isTreated';
  const order = searchParam.get('order') || 'asc';

  const {
    handlePaginationChange,
    handleColumnSort,
    countSelectedRows,
    // isRowSelected,
    // selectedRows,
    dispatch: useTableDispatch,
  } = useTable({
    setParams: (/** @type {object} */ params) => setSearchParams((searchParams) => {
      Object.entries(params).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
      return searchParams;
    }),
    order,
    sort,
  });

  const { mutate: mutateExcludeGarage } = useMutation({
    mutationFn: (/** @type {GarageExclusion} */ data) => {
      const { id } = data;
      return postExcludeGarage(id);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_GARAGE_EXCLUSIONS],
      });
    },
  });

  const { mutate: mutateReintegrateGarage } = useMutation({
    mutationFn: (/** @type {GarageExclusion} */ data) => {
      const { id } = data;
      return postReintegrateGarage(id);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_GARAGE_EXCLUSIONS],
      });
    },
  });

  // Services
  const {
    data: dataGarageExclusionsList,
    isLoading: isLoadingDataGarageExclusionList,
  } = useGetGarageExclusionsList(
    { pageNumber: Number(pageNumber), pageSize: Number(pageSize), sort: `${sort}:${order}` },
    { keepPreviousData: true },
  );

  // States
  /**
   * Pagination count label.
   * @param {number} currentPageMin
   * @param {number} currentPageMax
   * @param {number} count
   * @returns {string}
   */
  const paginationCountLabel = (
    currentPageMin,
    currentPageMax,
    count,
  ) => t('cortizol.pagination.resultByPage', {
    currentPageMin,
    currentPageMax,
    count,
  });

  // Handlers
  /**
   * Handles the status ban.
   * @param {GarageExclusion} row
   * @returns {void}
   */
  const handleExcludeGarage = (row) => {
    mutateExcludeGarage(row);
  };

  /**
   * Handles the status visible.
   * @param {GarageExclusion} row
   * @returns {void}
   */
  const handleReintegrateGarage = (row) => {
    mutateReintegrateGarage(row);
  };

  return (
    <Box direction="column" gap="s" className="padding-l">
      <PageHeader
        title={(
          <>
            <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
              {t('garageExclusionsList.title')}
            </h1>
            {dataGarageExclusionsList?.garageExclusions.length ? (
              <span className="font-color-neutral-500">
                {t('garageExclusionsList.nbRecords', {
                  count: dataGarageExclusionsList.garageExclusions.length,
                })}
              </span>
            ) : null}
          </>
        )}
      />

      {countSelectedRows ? (
        <Box align="center" justify="flex-end" fullWidth>
          {t('garageExclusionsList.nbRecordsSelected', {
            count: countSelectedRows,
          })}
          <Button
            color="noBackground"
            size="small"
            onClick={() => useTableDispatch({ type: 'RESET_SELECTED_ROWS' })}
          >
            {t('garageExclusionsList.reset')}
          </Button>
        </Box>
      ) : null}

      {isLoadingDataGarageExclusionList ? (
        <TableSkeleton />
      ) : (
        generateTableFromSchema({
          JSONSchema: GarageExclusionListSchema,
          data: dataGarageExclusionsList.garageExclusions,
          sort: {
            order,
            active: sort,
            handleColumnSort,
          },
          labelNoResult: t('garageExclusionsList.noResult'),
          customColumns: [
            {
              key: 'garageName',
              description: t('garageExclusionsList.garageName'),
              sortable: false,
              position: 'start',
              renderHead: () => t('garageExclusionsList.garageName'),
            },
            {
              key: 'garageStreetName',
              description: t('garageExclusionsList.garageStreetName'),
              sortable: false,
              position: 'start',
              renderHead: () => t('garageExclusionsList.garageStreetName'),
              renderCell: (/** @type {GarageExclusion} */ row) => (
                <Box className="margin-s">
                  {row?.garageStreetName
                    ? `${row?.garageStreetNumber} ${row?.garageStreetName}, 
                    ${row?.garagePostalCode} ${row?.garageCity}`
                    : '-'}
                </Box>
              ),
            },
            {
              key: 'exclusionReason',
              description: t('garageExclusionsList.exclusionReason'),
              sortable: false,
              position: 'end',
              renderHead: () => t('garageExclusionsList.exclusionReason'),
              renderCell: (/** @type {GarageExclusion} */ row) => (
                <Box className="margin-s">
                  {row?.exclusionReason
                    ? t(`garageExclusionsList.reasons.${row?.exclusionReason}`)
                    : '-'}
                </Box>
              ),
            },
            {
              key: 'requestedBy',
              description: t('garageExclusionsList.requestedBy'),
              sortable: false,
              position: 'end',
              renderHead: () => t('garageExclusionsList.requestedBy'),
              renderCell: (/** @type {GarageExclusion} */ row) => (
                <Box className="margin-s">
                  {row?.requestedBy
                    ? t(`garageExclusionsList.requester.${row?.requestedBy}`)
                    : '-'}
                </Box>
              ),
            },
            {
              key: 'actions',
              description: '-',
              type: 'custom',
              sortable: false,
              position: 'end',
              renderHead: () => '',
              renderCell: (/** @type {GarageExclusion} */ row) => (
                <Box justify="center">
                  <Popover
                    minWidth
                    animation="fade"
                    placement="left"
                    offset={[0, 0]}
                    content={(
                      <Box direction="column">
                        <Button
                          size="small"
                          color="noBackground"
                          icon="danger"
                          onClick={() => handleExcludeGarage(row)}
                        >
                          {t('garageExclusionsList.ban')}
                        </Button>
                        <Button
                          size="small"
                          color="noBackground"
                          icon="add"
                          onClick={() => handleReintegrateGarage(row)}
                        >
                          {t('garageExclusionsList.visible')}
                        </Button>
                      </Box>
                    )}
                  >
                    <Picto
                      className="font-color-primary-500"
                      icon="more"
                      style={{ height: '22px' }}
                    />
                  </Popover>
                </Box>
              ),
            },
          ],
        })
      )}

      {dataGarageExclusionsList?.garageExclusions.length
      && dataGarageExclusionsList?.pagination.recordsCount ? (
        <Pagination
          recordsCount={dataGarageExclusionsList.pagination.recordsCount}
          pageNumber={+pageNumber}
          pageSize={+pageSize}
          onPageNumberChange={handlePaginationChange}
          countLabelCallback={paginationCountLabel}
          perPageResultLabel={t('cortizol.pagination.resultPerPage')}
        />
        ) : null}
    </Box>
  );
}

export default GarageExclusionList;
